/** @file Removal helper: drops references to removed ids from animations, direction fixes and mix defaults. */
import type { Doc } from '../doc/schema';
import type { EntityKind } from '../doc/entityKinds';
import type { AnimationDoc, DirectionEntry } from '../doc/schemaParts/animation';
import { drawOrderOpSlots } from '../doc/drawOrderOpSlots';
import { mixNames } from '../doc/mixNames';
import { names } from '../names';
import { dropKeys } from './dropKeys';
import { withoutRemoved } from './withoutRemoved';

type Removed = ReadonlyMap<string, EntityKind>;

function stripAnimation(anim: AnimationDoc, removed: Removed): void {
  const gone = (id: string): boolean => removed.has(id);
  for (const table of [anim.bones, anim.slots, anim.ik, anim.deform]) dropKeys(table, gone);
  for (const key of anim.drawOrder) {
    const order = key.order && withoutRemoved(key.order, gone);
    if (order) key.order = order;
  }
  const events = withoutRemoved(anim.events, (e) => gone(e.event));
  if (events) anim.events = events;
}

function stripFix(entry: DirectionEntry | undefined, removed: Removed): void {
  const fix = entry?.fix;
  if (!fix) return;
  if (fix.skin !== undefined && removed.has(fix.skin)) delete fix.skin;
  const ops = fix.drawOrderOps && withoutRemoved(fix.drawOrderOps, (op) => drawOrderOpSlots(op).some((s) => removed.has(s)));
  if (ops) fix.drawOrderOps = ops;
  if (fix.animations) dropKeys(fix.animations, (_base, animId) => typeof animId === 'string' && removed.has(animId));
}

/** Drops timelines and keys of removed ids from surviving animations, direction fixes and mix defaults. */
export function stripAnimationRefs(draft: Doc, removed: Removed): void {
  for (const [id, anim] of Object.entries(draft.animations)) if (!removed.has(id)) stripAnimation(anim, removed);
  const entries = draft.directions?.entries;
  if (entries) for (const dir of names.DIRS) stripFix(entries[dir], removed);
  if (!draft.mixDefaults || ![...removed.values()].includes('animation')) return;
  const known = mixNames(draft.animations, removed);
  dropKeys(draft.mixDefaults, (pair) => pair.split('>').some((name) => !known.has(name)));
}
